const SUCCESS_STATUSES = new Set(['SYNCED', 'SUCCESS', 'LINKED'])
const ERROR_STATUSES = new Set(['ERROR', 'ERROR_UNLINKED', 'ERROR_NO_MAPPING', 'SYNCED_WITH_ERROR'])
const IN_PROGRESS_STATUSES = new Set(['PENDING', 'PROCESSING', 'PENDING_APPROVAL'])

export const normalizeGridSyncStatus = (value: any): string => {
  const status = String(value || '').trim().toUpperCase()
  return status === 'LINKED' ? 'SYNCED' : status
}

export const getBundleItems = (row: any): any[] => {
  const items = row?.bundle?.items
  return Array.isArray(items) ? items : []
}

const getChildStatusValue = (item: any, field: string) => {
  const nestedVariant = item?.platform_product_variant
  const candidates = [
    item?.[field],
    nestedVariant?.[field],
    item?.sync_status,
    nestedVariant?.sync_status,
  ]

  return candidates.find((value) => value !== null && value !== undefined && value !== '')
}

const getChildStatusMessage = (item: any, field: string) => {
  const nestedVariant = item?.platform_product_variant
  const messageField = field === 'sync_status' ? 'sync_message' : field.replace(/_status$/, '_message')
  const candidates = [
    item?.[messageField],
    nestedVariant?.[messageField],
    item?.sync_message,
    nestedVariant?.sync_message,
  ]

  return candidates.find((value) => value !== null && value !== undefined && value !== '')
}

export const getBundleChildStatuses = (row: any, field: string): string[] => {
  return getBundleItems(row)
    .map((item) => normalizeGridSyncStatus(getChildStatusValue(item, field)))
    .filter(Boolean)
}

export const hasBundleChildStatuses = (row: any, field: string): boolean => {
  return getBundleChildStatuses(row, field).length > 0
}

export const resolveBundleStatus = (row: any, field: string, fallbackStatus: any) => {
  const childStatuses = getBundleChildStatuses(row, field)
  if (childStatuses.length === 0) {
    return fallbackStatus
  }

  const hasError = childStatuses.some((status) => ERROR_STATUSES.has(status))
  const hasSuccess = childStatuses.some((status) => SUCCESS_STATUSES.has(status))
  const hasInProgress = childStatuses.some((status) => IN_PROGRESS_STATUSES.has(status))
  const allSuccess = childStatuses.every((status) => SUCCESS_STATUSES.has(status))
  const allSame = childStatuses.every((status) => status === childStatuses[0])

  if (allSuccess) return 'SYNCED'
  if (hasError && hasSuccess) return 'SYNCED_WITH_ERROR'
  if (hasError) return childStatuses.find((status) => ERROR_STATUSES.has(status)) || 'ERROR'
  if (hasSuccess && hasInProgress) return 'SYNCED_PARTIALLY'
  if (hasInProgress) return childStatuses.includes('PROCESSING') ? 'PROCESSING' : 'PENDING'
  if (allSame) return childStatuses[0]

  return 'SYNCED_PARTIALLY'
}

const RESYNC_ELIGIBLE_STATUSES = new Set(['PROCESSING', 'ERROR', 'ERROR_UNLINKED', 'ERROR_NO_MAPPING'])

export const isRowResyncEligible = (rowData: any): boolean => {
  if (!rowData) return false

  const fieldsToCheck = new Set([
    'sync_status',
    'primary_sync_status',
    'accounting_sync_status',
    'erp_sync_status',
    'shipping_sync_status',
    'inventory_sync_status',
    'secondary_sync_status',
  ])

  for (const key in rowData) {
    if (key.endsWith('_sync_status')) {
      fieldsToCheck.add(key)
    }
  }

  for (const field of fieldsToCheck) {
    const fallback = rowData?.[field]
    const resolved = resolveBundleStatus(rowData, field, fallback)
    const val = String(resolved || '').trim().toUpperCase()
    if (RESYNC_ELIGIBLE_STATUSES.has(val)) return true
  }

  return false
}

export const resolveBundleStatusMessage = (row: any, field: string, fallbackMessage?: string) => {
  const childMessages = getBundleItems(row)
    .map((item, index) => {
      const status = normalizeGridSyncStatus(getChildStatusValue(item, field))
      const message = getChildStatusMessage(item, field)
      if (!status && !message) return ''

      const name = item?.name || item?.platform_product_variant?.name || `Child item ${index + 1}`
      return `${name}: ${message || status}`
    })
    .filter(Boolean)

  if (childMessages.length === 0) {
    return fallbackMessage
  }

  return childMessages.join('\n')
}
